/**
 * Connect transport to the Go backend.
 *
 * The read path is migrating off REST onto Connect RPC. This module owns the
 * single transport every migrated method calls through, plus a thin `rpc`
 * helper that hides the low-level `transport.unary` signature.
 */
import type {
  DescMessage,
  DescMethodUnary,
  MessageInitShape,
  MessageShape,
} from '@bufbuild/protobuf';
import type { Interceptor, Transport } from '@connectrpc/connect';
import { Code, ConnectError } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';

import { apiOrigin } from './origin';
import { accessToken, expireSession } from './session';

/**
 * Attaches the bearer token to every call (RFC 6750). Every procedure is
 * guarded, so there is no list of exceptions to keep in step.
 */
const attachAccessToken: Interceptor = (next) => async (request) => {
  const token = accessToken();
  if (token) {
    request.header.set('authorization', `Bearer ${token}`);
  }
  return next(request);
};

/**
 * Clears a token the server has refused, so the admin guard shows the login
 * form again instead of every panel failing on its own.
 */
const dropRejectedSession: Interceptor = (next) => async (request) => {
  try {
    return await next(request);
  } catch (error) {
    if (error instanceof ConnectError && error.code === Code.Unauthenticated && accessToken()) {
      expireSession();
    }
    throw error;
  }
};

export const transport: Transport = createConnectTransport({
  baseUrl: apiOrigin,
  interceptors: [dropRejectedSession, attachAccessToken],
});

/**
 * Invoke a unary Connect method and return the decoded response message.
 */
export async function rpc<I extends DescMessage, O extends DescMessage>(
  method: DescMethodUnary<I, O>,
  input?: MessageInitShape<I>,
  /**
   * Abandons the call. Worth passing for anything the server works hard at —
   * a caller that has asked again has stopped wanting the first answer, and
   * without this the first request runs to completion regardless.
   */
  signal?: AbortSignal,
): Promise<MessageShape<O>> {
  const response = await transport.unary(
    method,
    signal,
    undefined,
    undefined,
    input ?? ({} as MessageInitShape<I>),
  );
  return response.message;
}
